import { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/auth';
import { buildLoginPathWithRedirect } from '../utils/loginRedirect';

/**
 * 未認証ユーザーをログイン画面へ退避させる共通フック（#3203）。
 *
 * 元のURL（代理ログインの ?proxy= を含む）を ?redirect= に引き継ぐため、
 * ログイン後にそのページへ復帰できる。
 * 従来は各画面が `navigate('/')` を個別に持っており、search が捨てられるため
 * 共有された代理ログインのリンクを未ログインで開くと ?proxy= が失われていた（#3184 の退行）。
 *
 * 認証が確定する前（isLoading 中）は判定しない。確定前に遷移すると、
 * 認証済みユーザーまでログイン画面へ飛ばしてしまうため。
 */
export const useAuthRedirect = (): void => {
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // このセッションで一度でも認証済みだったか。
  // ログアウト（認証済み → 未認証）の場合に ?redirect= を付けないための判定に使う。
  const wasAuthenticatedRef = useRef(false);

  useEffect(() => {
    if (isLoading) return;

    if (user !== null) {
      wasAuthenticatedRef.current = true;
      return;
    }

    // ログアウト直後は元のURLを引き継がない。
    // signOut で user が null になった時点ではまだ元のページにいるため、
    // ここで ?redirect= を付けると再ログイン後に直前の画面へ戻ってしまう。
    // ログアウトは「セッションを終わらせる」操作であり、各画面の navigate('/') に任せる。
    if (wasAuthenticatedRef.current) return;

    navigate(buildLoginPathWithRedirect(location.pathname, location.search, location.hash), {
      replace: true,
    });
  }, [user, isLoading, navigate, location.pathname, location.search, location.hash]);
};
